// src/utils/teams.js
//
// Teams are plain names on the org (`organizations/{id}.groups`), and a name
// is also the team's id in every member record, so a rename is a migration.
// The convention that makes the names usable by a 12-year-old picking from a
// list: the DIVISION comes first ("U15 AA Leduc Oil Kings"). Everything here
// reads that convention; nothing requires it, a name without a division
// simply files under "Other".

const DIVISION_RE = /^U(\d{2})\b/i;

export const divisionOf = (teamName) => {
    const m = DIVISION_RE.exec(String(teamName || '').trim());
    return m ? `U${m[1]}` : 'Other';
};

// Divisions present in a list of teams, youngest first, "Other" last.
export const divisionsIn = (groups = []) => {
    const set = new Set((groups || []).map(divisionOf));
    const list = [...set].filter(d => d !== 'Other').sort((a, b) => parseInt(a.slice(1), 10) - parseInt(b.slice(1), 10));
    if (set.has('Other')) list.push('Other');
    return list;
};

export const teamsInDivision = (groups = [], division) => (groups || []).filter(g => divisionOf(g) === division);

// Hockey Alberta's bands: U13 is 11-12, U15 is 13-14, U18 is 15-17. Call-ups
// and late birthdays happen, so this is a note, never a block.
// U16 and U17 are leagues of their own in Alberta (U16 AA, U17 AAA); they keep
// their own label in the team name rather than filing under U18.
const AGE_BANDS = { U13: [10, 13], U15: [12, 15], U16: [13, 17], U17: [14, 18], U18: [14, 18] };
export const ageNoteFor = (division, age) => {
    const band = AGE_BANDS[division];
    const a = parseInt(age, 10);
    if (!band || isNaN(a)) return null;
    if (a >= band[0] && a <= band[1]) return null;
    return `${division} teams are usually for players aged ${band[0] + 1} to ${band[1] - 1}. If this is your team, carry on; if not, check the link your coach sent.`;
};

// The per-team join link: a PATH (/join/{org}/{team}), which App.jsx turns into
// the hash route on load. A path survives Excel, mail clients and SMS previews,
// where a '#' in a link often does not. The join screen pre-selects and locks
// the team.
export const teamJoinLink = (orgId, team, origin = (typeof window !== 'undefined' ? window.location.origin : '')) =>
    `${origin}/join/${orgId}/${encodeURIComponent(team)}`;

// A team's STAFF link: the team link plus the organization's staff code. Bench
// staff (assistants, the manager, a division director) join from it and land on
// the coaches roster for that team (functions/modules/staffRules.js), so it goes
// to staff only. The code is the organization's, rotatable in the org editor.
export const staffJoinLink = (orgId, team, code, origin = (typeof window !== 'undefined' ? window.location.origin : '')) =>
    `${teamJoinLink(orgId, team, origin)}/staff/${encodeURIComponent(String(code || '').trim().toUpperCase())}`;

// Eight characters from an alphabet without 0/O/1/I, so a code survives being
// read aloud over a bench. 256 is a multiple of 32, so the modulo is unbiased.
const CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
export const newStaffCode = (length = 8) => {
    const bytes = new Uint8Array(length);
    if (typeof crypto !== 'undefined' && crypto.getRandomValues) crypto.getRandomValues(bytes);
    else for (let i = 0; i < length; i++) bytes[i] = Math.floor(Math.random() * 256);
    return Array.from(bytes, b => CODE_ALPHABET[b % CODE_ALPHABET.length]).join('');
};

// How the import names a team from the roster template's columns.
export const teamNameFrom = ({ division, tier, association, team }) =>
    [division, tier, association, team].map(s => String(s || '').trim()).filter(Boolean).join(' ');
